import { Card, CardContent } from "~/components/ui/card";
import { ChevronDown, ChevronUp, Clock, MapPin, Link } from "lucide-react";
import { useState } from "react";
import { cn } from "~/lib/utils";
import { Button } from "~/components/ui/button";

export interface RoleAttendance {
    label: string;
    percentage: number;
}

export interface ExpandableEventTileProps {
    title: string;
    date: string;
    time: string;
    location?: string;
    selectedRoles: RoleAttendance[];
    eventUrl?: string;
    isExpanded?: boolean;
    onExpandToggle?: () => void;
    onSignUpClick?: () => void;
}

export function ExpandableEventTile({
    title,
    date,
    time,
    location,
    selectedRoles,
    eventUrl,
    isExpanded = false,
    onExpandToggle,
    onSignUpClick
}: ExpandableEventTileProps) {
    const [expanded, setExpanded] = useState(isExpanded);

    const handleExpand = () => {
        const newExpanded = !expanded;
        setExpanded(newExpanded);
        onExpandToggle?.();
    };

    return (
        <Card className="w-full max-w-[320px] mx-auto">
            <CardContent className="p-4">
                <div className="space-y-3 text-center">
                    <h3 className="font-semibold">{title}</h3>

                    {selectedRoles.length > 0 && (
                        <div className="space-y-2">
                            {selectedRoles.map((role) => (
                                <div key={role.label} className="text-sm text-muted-foreground">
                                    {role.label}: {role.percentage}% of attendees
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="space-y-2">
                        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                            <Clock className="h-4 w-4 shrink-0" />
                            <span>{date} @ {time}</span>
                        </div>

                        <div
                            className={cn(
                                "space-y-2 overflow-hidden transition-all duration-300",
                                expanded ? "max-h-[500px]" : "max-h-0"
                            )}
                        >
                            {location && (
                                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                                    <MapPin className="h-4 w-4 shrink-0" />
                                    <span>{location}</span>
                                </div>
                            )}
                            {eventUrl && (
                                <div className="flex items-center justify-center gap-2">
                                    <Link className="h-4 w-4 shrink-0" />
                                    <a
                                        href={eventUrl}
                                        className="text-sm text-blue-600 hover:underline"
                                    >
                                        Event Page
                                    </a>
                                </div>
                            )}

                            <div className="pt-2 space-y-2">
                                <p className="text-sm text-muted-foreground">
                                    Use bromii to learn which attendees are most relevant for you.
                                </p>
                                <a href="/login">
                                    <Button
                                        variant="landing"
                                        className="mt-2"
                                        onClick={onSignUpClick}
                                    >
                                        Signup | Login
                                    </Button>
                                </a>
                            </div>
                        </div>
                    </div>

                    <button
                        onClick={handleExpand}
                        className="w-full flex items-center justify-center"
                    >
                        {expanded ? (
                            <ChevronUp className="h-4 w-4 text-muted-foreground" />
                        ) : (
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        )}
                    </button>
                </div>
            </CardContent>
        </Card>
    );
} 